/**
 * AIF-03 — Step 1: pick the data acquisition (§11.5; discharges AIF-02 too).
 *
 * One combined search box over `getDataAcquisitions.q` (matches flight_code,
 * turbine identifier, park number, site, …). The picker is hard-restricted to
 * the importable states (PRE_SCREENING / EXPERT_ANALYSIS) — that filter is
 * applied in the query hook, not here. Columns per the brief: Code
 * (flightCode), Turbine (assetIdentifier), Park # (internalParkNumber), Site
 * (siteName), State, Flight date (flightDate). Row select → onSelect.
 */
import { useState, type ReactElement } from "react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { SearchField } from "@aeriosiq/ui";
import type { DataAcquisition } from "@aeriosiq/types";
import { useImportDaSearch } from "./importQueries.js";
import { formatState } from "./labels.js";

export interface Step1PickDaProps {
  selectedId: string | null;
  onSelect: (da: DataAcquisition) => void;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toISOString().slice(0, 10);
}

export function Step1PickDa({
  selectedId,
  onSelect,
}: Step1PickDaProps): ReactElement {
  const [search, setSearch] = useState("");
  const query = useImportDaSearch(search);
  const items: DataAcquisition[] = query.data?.items ?? [];

  return (
    // R0627-09 — fill the wizard's body height: the search box stays pinned
    // while the table region grows to consume the remaining viewport and owns
    // its own scroll (sticky header keeps the column row visible).
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        minHeight: 0,
      }}
    >
      <SearchField
        value={search}
        onChange={setSearch}
        placeholder="Search by flight code / turbine identifier / park number"
        ariaLabel="Search data acquisitions"
      />
      <TableContainer sx={{ flex: 1, minHeight: 0 }}>
        <Table size="small" stickyHeader aria-label="Data acquisitions">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Turbine</TableCell>
              <TableCell>Park #</TableCell>
              <TableCell>Site</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Flight date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {query.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={20} aria-label="Loading" />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    No data acquisitions in PRE_SCREENING or EXPERT_ANALYSIS
                    match your search.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((da) => {
                const selected = da.id === selectedId;
                return (
                  <TableRow
                    key={da.id}
                    hover
                    selected={selected}
                    onClick={() => {
                      onSelect(da);
                    }}
                    sx={{ cursor: "pointer" }}
                    aria-selected={selected}
                    data-testid="da-row"
                  >
                    <TableCell>{da.flightCode ?? "—"}</TableCell>
                    <TableCell>{da.assetIdentifier ?? "—"}</TableCell>
                    <TableCell>{da.internalParkNumber ?? "—"}</TableCell>
                    <TableCell>{da.siteName ?? "—"}</TableCell>
                    <TableCell>{formatState(da.state)}</TableCell>
                    <TableCell>{fmtDate(da.flightDate)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
